import styled, { css } from 'styled-components/macro'

import { styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'
import { Container } from 'components/container'
import { Logo as LogoCT } from 'components/icons/logo'
import { motion } from 'framer-motion'
import { typography } from 'styles/typography'

export const StyledHeader = styled(motion.header)<{ $isSticky: boolean }>`
  width: 100%;
  padding-top: env(safe-area-inset-top);
  font-size: 1.6rem;
  color: var(--text-color);
  background: var(--background);

  > ${Container} {
    display: flex;
    align-items: center;
    height: calc(var(--header-height) - env(safe-area-inset-top));
  }

  ${mq.ltLarge} {
    z-index: 2000;

    ${(props) =>
      !props.$isSticky &&
      css`
        position: relative;
      `}
  }

  ${(props) =>
    props.$isSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 2000;
    `}
`

export const Logo = styled(LogoCT)`
  height: 6rem;
  width: auto;
  display: block;
  overflow: hidden;

  ${mq.large} {
    height: 7.4rem;
  }
`

export const LogoContainer = styled.a`
  display: block;
  width: max-content;
  color: inherit;
  overflow: hidden;
  margin-inline-end: auto;

  &:hover,
  &:focus {
    color: inherit;
  }
`

export const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  right: var(--container-spacing);
  width: 1.9rem;
  height: var(--header-height);
  box-sizing: content-box;
  flex-shrink: 0;
  transition: 0.4s opacity 0.55s;
  z-index: 600;

  ${mq.medium} {
    width: auto;
  }
`

export const CloseButton = styled.li`
  margin-top: -0.1em;
  padding: 0.8rem;

  button {
    ${styleReset}
    display: block;
    position: relative;
    cursor: pointer;
    width: 1.6rem;
    height: 1.6rem;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      border-top: 2px solid white;
      transform: rotateZ(45deg);
    }

    &::after {
      transform: rotateZ(-45deg);
    }
  }

  ${mq.smallOnly} {
    button::before,
    button::after {
      border-top-color: ${theme.colors.secondary};
    }
  }
`

export const Menu = styled(motion.ul)`
  ${typography.body}
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  list-style: none;
  font-weight: 500;

  ${mq.large} {
    flex-direction: row;
    gap: var(--spacing-sm);
  }

  a,
  button {
    ${styleReset}
    position: relative;
    display: flex;
    align-items: center;
    width: max-content;
    padding: 0.8em var(--spacing-sm);
    text-decoration: none;

    ${mq.large} {
      padding: 0;
      height: var(--header-height);
    }
  }

  button:disabled,
  a:hover,
  a:focus-visible {
    color: ${theme.colors.theme};
  }

  button:disabled::after {
    content: '';
    display: block;
    position: absolute;
    background-color: currentColor;

    ${mq.ltLarge} {
      left: 0;
      top: 0;
      bottom: 0;
      width: 6px;
      border-bottom-right-radius: 3px;
      border-top-right-radius: 3px;
    }

    ${mq.large} {
      bottom: 0;
      left: -1rem;
      right: -1rem;
      height: 6px;
      border-top-left-radius: 3px;
      border-top-right-radius: 3px;
    }
  }
`

export const MenuItem = styled.li`
  position: relative;
`

export const IconDefault = styled.svg`
  font-size: 2.4rem;
  display: block;

  button:disabled &,
  a:hover &,
  a:focus-visible & {
    display: none;
  }
`

export const IconActive = styled.svg`
  font-size: 2.4rem;
  display: none;

  button:disabled &,
  a:hover &,
  a:focus-visible & {
    display: block;
  }
`

export const ShareMenu = styled(Menu)`
  flex-wrap: wrap;

  ${mq.ltLarge} {
    flex-direction: row;
    align-items: center;
    margin-top: 1rem;
    border-top: 1px solid rgb(0 0 0 / 25%);

    > li {
      width: min-content;
    }

    &::before {
      content: 'Share:';
      display: block;
      width: 100%;
      margin: var(--spacing-sm) var(--spacing-sm) -0.8rem;
    }
  }
`

export const ShareButton = styled(motion.button)`
  ${styleReset}
  margin: 1rem 0 1rem var(--spacing-sm);
  font-size: 2.4rem;
  height: calc(100% - 2rem);
  border-left: 1px solid rgb(0 0 0 / 25%);
  padding-left: var(--spacing-sm);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${theme.colors.theme};
  }
`

export const ShareItem = styled.a`
  display: flex;
  cursor: pointer;

  &:first-child {
    margin-left: -0.8rem;
  }

  svg {
    font-size: 4rem;
  }
`

export const MobileMenuContainer = styled(motion.div)`
  position: absolute;
  top: var(--header-height);
  left: 0;
  width: 100%;
  max-height: calc(100dvh - var(--header-height));
  overflow-y: auto;
  -webkit-overflow-scrolling: contain;
  background: white;
  z-index: 1500;

  ${mq.large} {
    display: contents;
  }
`

export const MenuToggle = styled.button<{ $isVisible: boolean }>`
  ${styleReset}
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: center;
  cursor: pointer;

  svg {
    font-size: 3.2rem;
  }
`

export const Submenu = styled.ul`
  --border-radius: 0.4em;

  padding: 0;
  margin: 0;
  list-style: none;
  z-index: 50;

  &::before {
    content: '';
    display: block;
    position: absolute;
    bottom: calc(100% - 2rem);
    right: var(--border-radius);
    width: 3.2rem;
    aspect-ratio: 1 / 2;
    background: url("data:image/svg+xml,%3Csvg width='32' height='16' viewBox='0 0 32 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.0049 0.413086C16.224 9.05873 23.3016 16 32 16H0C8.83656 16 16 8.83656 16 0L16.0049 0.413086Z' fill='%23ffffff'/%3E%3C/svg%3E%0A")
      center bottom / contain no-repeat;
  }

  ${mq.large} {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    padding: 2rem 0 0;
    margin: 0 -1.2rem;

    ${MenuItem}:hover &, ${MenuItem}:focus-visible &, ${MenuItem}:focus-within & {
      display: block;
    }
  }
`

export const SubmenuItem = styled.li`
  padding: 0;
  margin: 0;
  background-color: ${theme.colors.background};

  ${mq.large} {
    ${theme.shadow()}
    padding: 0 0.8em;
  }

  &:first-child {
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
  }

  &:last-child {
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
  }

  button {
    --inset-left: calc(2.4 * var(--spacing-sm));

    ${styleReset}
    display: block;
    height: auto;
    padding: 0 var(--spacing-sm) 0.8em var(--inset-left);
    cursor: pointer;

    ${mq.ltLarge} {
      position: relative;
      width: 100vw;

      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 0.8em;
        left: var(--inset-left);
        width: 0.8rem;
        height: 0.8rem;
        margin: -0.4rem -1.4rem;
        border-radius: 33.333%;
        background-color: ${theme.colors.theme};
      }
    }

    ${mq.large} {
      width: 20em;
      padding: 0.6em 0;
      font-size: 1.5rem;
    }

    &:hover,
    &:focus-visible {
      color: ${theme.colors.theme};
    }
  }

  ${mq.large} {
    &:not(:first-child) button {
      border-top: 1px dashed currentColor;
    }
  }
`

export const Backdrop = styled(motion.button)`
  ${styleReset}
  position: fixed;
  inset: var(--header-height) 0 0;
  background-color: rgb(0 0 0 / 10%);
`
